import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * @type {string | null}
   */
  #emptyResultsHTML = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    // Keep a local empty-state snapshot so reset does not wait on the network.
    if (this.refs.predictiveSearchResults && this.#emptyResultsHTML == null) {
      this.#emptyResultsHTML = this.refs.predictiveSearchResults.innerHTML;
    }

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    // Hard-block Enter / form submit so the modal never navigates to /search.
    // Enter instead runs predictive search and shows results in the popup.
    const form = this.querySelector('form.predictive-search-form');
    form?.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.runModalSearch();
      },
      { capture: true, signal }
    );
    this.refs.searchInput?.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter' || event.code === 'Enter' || event.keyCode === 13) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.runModalSearch();
        }
      },
      { capture: true, signal }
    );

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
      this.addEventListener('click', this.#handleSearchTabClick, { signal });
    }

    // Search immediately if the user typed/pressed Enter before the module upgraded.
    const pending = typeof window.__pendingPredictiveQuery === 'string' ? window.__pendingPredictiveQuery.trim() : '';
    const current = this.refs.searchInput?.value?.trim() || '';
    const bootQuery = pending || current;
    if (bootQuery) {
      window.__pendingPredictiveQuery = '';
      if (this.refs.searchInput && !current) {
        this.refs.searchInput.value = bootQuery;
      }
      this.runModalSearch();
    }
  }

  /**
   * Handles Product / Blog tab clicks inside search results.
   * @param {MouseEvent} event
   */
  #handleSearchTabClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const tab = target.closest('[data-search-tab]');
    if (!(tab instanceof HTMLElement) || tab.hasAttribute('disabled')) return;
    if (!this.contains(tab)) return;

    const tabName = tab.getAttribute('data-search-tab');
    if (!tabName) return;

    event.preventDefault();

    this.querySelectorAll('[data-search-tab]').forEach((el) => {
      const isActive = el.getAttribute('data-search-tab') === tabName;
      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.querySelectorAll('[data-search-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      panel.hidden = panel.getAttribute('data-search-panel') !== tabName;
    });
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    // Keep search in the modal — Enter is handled in capture listener (runModalSearch).
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;
    }
  };

  /**
   * Prevent native form submit (e.g. Enter) so the modal does not jump to /search.
   * @param {SubmitEvent} event
   */
  onSearchSubmit = (event) => {
    event.preventDefault();
    this.runModalSearch();
  };

  /**
   * Run predictive search in the popup (no page navigation).
   */
  runModalSearch = () => {
    const searchTerm = this.refs.searchInput?.value?.trim() || '';
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  };

  /**
   * Apply fetched markup into the results container only (never the whole form/page).
   * Always leaves a visible no-results state if markup cannot be parsed.
   * @param {string} html
   * @param {HTMLElement} target
   * @param {string} [searchTerm]
   */
  #applyResultsMarkup(html, target, searchTerm = '') {
    if (!(target instanceof HTMLElement)) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const resultsElement =
      doc.querySelector('#predictive-search-results') ||
      doc.querySelector('.predictive-search-dropdown') ||
      doc.querySelector('[id^="shopify-section-"] #predictive-search-results') ||
      doc.querySelector('[id^="shopify-section-"] .predictive-search-dropdown');

    if (resultsElement) {
      target.replaceChildren(resultsElement.cloneNode(true));
      return;
    }

    const safeTerm = String(searchTerm || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    target.innerHTML = `
      <div id="predictive-search-results" class="predictive-search-dropdown" data-search-results>
        <div class="predictive-search-results__inner">
          <p class="predictive-search-results__no-results">
            No results found${safeTerm ? ` for “${safeTerm}”` : ''}.
          </p>
        </div>
      </div>
    `;
  }

  /**
   * Navigate to full search results, or trending collection when query is empty.
   * @param {MouseEvent} [event]
   */
  viewAllResults = (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    const searchTerm = this.refs.searchInput?.value?.trim() || '';
    const root = window.Shopify?.routes?.root || '/';

    if (searchTerm.length > 0) {
      const searchUrl = new URL(Theme.routes.search_url, location.origin);
      searchUrl.searchParams.set('q', searchTerm);
      searchUrl.searchParams.set('type', 'product,article');
      window.location.assign(searchUrl.toString());
      return;
    }

    // Empty query (Trending Now): go to OTC Hearing Aids collection
    window.location.assign(`${root}collections/otc-hearing-aids`);
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 80);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('section_id', this.dataset.sectionId);
    url.searchParams.set('resources[type]', 'product,article');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');
    // Prefer title matches so results stay relevant without breaking the suggest API.
    url.searchParams.set('resources[options][fields]', 'title');

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    try {
      const response = await fetch(url.toString(), {
        signal: abortController.signal,
        headers: { Accept: 'text/html' },
      });

      if (abortController.signal.aborted) return;

      if (!response.ok) {
        this.#applyResultsMarkup('', predictiveSearchResults, searchTerm);
        return;
      }

      const resultsMarkup = await response.text();
      if (abortController.signal.aborted) return;

      this.#applyResultsMarkup(resultsMarkup, predictiveSearchResults, searchTerm);
      this.#resetScrollPositions();
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('[predictive-search]', error);
      this.#applyResultsMarkup('', this.refs.predictiveSearchResults, searchTerm);
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;

    this.#currentIndex = -1;
    if (searchInput) searchInput.value = '';
    this.#hideResetButton();
    this.#createAbortController();

    if (this.#emptyResultsHTML != null && predictiveSearchResults) {
      predictiveSearchResults.innerHTML = this.#emptyResultsHTML;
      this.#resetScrollPositions();
      return;
    }

    // Fallback: one network fetch if local snapshot is missing
    const emptySectionId = 'predictive-search-empty';
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    this.#applyResultsMarkup(emptySectionMarkup, predictiveSearchResults);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
